/* eslint-disable complexity */

import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { UILoader } from '@futrli/ui-components';
import { Forecast } from '@futrli-sdk/predict';
import { ROUTES } from '@futrli/shared-services';

import { AvailableTemplates } from '../../components/available-templates/available-templates';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';
import { trackCTA, trackEvent } from '../../../../../../../core/tracker';
import { PredictManagementContext } from '../../../../../../../predict-management/context/predict-management-context';
import { ImportModal } from '../../components/import-modal/import-modal';
import { ImportPageWrapper } from '../../components/import-page-wrapper/import-page-wrapper';
import { ImportTitle } from '../../components/import-title/import-title';
import { Instructions } from '../../components/instructions/instructions';
import { Introduction } from '../../components/introduction/introduction';
import { sectionsAndTemplates, Template } from '../../templates';
import { useGoogleSheetsToken } from '../../../hooks/use-google-sheets-token/use-google-sheets-token';
import { useImportSheetInfo } from '../../../hooks/use-import-sheets-info/use-import-sheets-info';
import { usePredictionsInfoContext } from '../../../../../context/predictions-info-context';
import { getRedirectURL } from '../../../url-utils';

import styles from './import-organisation.scss';

const POST_AUTH_POLL_INTERVAL = 3000;

export const ImportOrganisation: FC = () => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;
  const { addErrorMessage, googleSheetImportAllowed } = useContext(PredictManagementContext);
  const { forecast, fetchForecast, isConsolidation } = usePredictionsInfoContext();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const hasCreatedImportSheet = !isConsolidation && !!(forecast as Forecast)?.import_sheet_id;
  const hasSynced = !isConsolidation && !!(forecast as Forecast)?.import_sheet_last_sync_at;

  const importRedirectURL = getRedirectURL(workspaceId, organisationId, forecastId!, ROUTES.IMPORT_REDIRECT);
  const { isLoading: isLoadingTokens, result: tokenData } = useGoogleSheetsToken(importRedirectURL);

  const { isLoading: isLoadingSheetInfo, result: sheetInfoData } = useImportSheetInfo(
    workspaceId,
    organisationId,
    forecastId,
    hasCreatedImportSheet
  );

  const isLoading = isLoadingTokens || isLoadingSheetInfo;
  const sheetInfo = !sheetInfoData?.error ? sheetInfoData?.data?.import_sheet_info : undefined;
  const isSheetAccessible = hasCreatedImportSheet && sheetInfo?.sheet_accessible;

  // Show error message if token data changes and there is an error
  useEffect(() => {
    if (tokenData?.error) {
      addErrorMessage?.(new Error(tokenData.error));
    }
  }, [addErrorMessage, tokenData]);

  // Show error message if sheet info data changes and there is an error
  useEffect(() => {
    if (sheetInfoData?.error) {
      addErrorMessage?.(new Error(sheetInfoData.error));
    }
  }, [addErrorMessage, sheetInfoData]);

  // Poll for updates to forecast import sheet ID.
  // We need to do this as we don't know how long it will take for a user
  // to complete initial authorisation and need to show the sync controls
  // once it has been completed in a separate tab.
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (hasCreatedImportSheet && pollIntervalRef.current) {
      // clear interval if user has created import sheet
      clearInterval(pollIntervalRef.current);
    }
  });

  useEffect(() => {
    return () => {
      // clear interval on unmount
      pollIntervalRef.current && window.clearInterval(pollIntervalRef.current);
    };
  }, []);

  function pollForecast() {
    const interval = window.setInterval(() => {
      fetchForecast && fetchForecast();
    }, POST_AUTH_POLL_INTERVAL);
    pollIntervalRef.current = interval;
  }

  const onClose = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    trackCTA('Import', 'Create master sheet');
    setIsModalOpen(true);
  };

  const openTemplate = (template: Template) => {
    trackEvent(TRACKING_EVENTS.IMPORT_OPEN_TEMPLATE, { templateName: template.linkName });
    window.open(template.link, '_blank');
  };

  const handleCancelClick = () => {
    navigate(-1);
  };

  if (isConsolidation || !googleSheetImportAllowed) {
    return null;
  }

  // show loading spinner if info loading
  // unless we are currently creating an import sheet in the open modal
  if ((isLoadingTokens || isLoading) && !isModalOpen) {
    return (
      <section data-testid="import-page-loading">
        <UILoader isFull />
      </section>
    );
  }

  return (
    <>
      <ImportPageWrapper forecastName={forecast?.name} onCancel={handleCancelClick}>
        <section className={styles.container} data-testid="import-page">
          {isModalOpen && <ImportModal onClose={onClose} onAuthRedirect={pollForecast} />}

          <div className="columns">
            <div className="column is-12">
              <ImportTitle
                isSheetAccessible={isSheetAccessible}
                sheetInfo={sheetInfo}
                showBeta
                title={<FormattedMessage id="import.header" defaultMessage="Google Sheets hot-link templates" />}
              />
            </div>
          </div>

          <Introduction
            hasCreatedImportSheet={hasCreatedImportSheet}
            isSheetAccessible={isSheetAccessible}
            sheetInfo={sheetInfo || null}
            hasValidAuthToken={!!tokenData?.hasToken}
            authURL={tokenData?.authUrl}
          />

          <Instructions openModal={openModal} hasCreatedImportSheet={hasCreatedImportSheet} hasSynced={hasSynced} />
          <AvailableTemplates
            data={sectionsAndTemplates}
            isSheetAccessible={isSheetAccessible}
            onOpenTemplate={openTemplate}
          />
        </section>
      </ImportPageWrapper>
    </>
  );
};
